import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/EmptyState";
import { fmtDateTime } from "@/lib/status";
import {
  Activity, KeyRound, ShieldAlert, FileText, Users, Settings2, Download, Search, RefreshCw,
} from "lucide-react";

const ACTION_META = {
  signup: { label: "created the organization", group: "team", icon: Users, tone: "ok" },
  email_verified: { label: "verified their email", group: "logins", icon: KeyRound, tone: "ok" },
  login_success: { label: "logged in", group: "logins", icon: KeyRound, tone: "ok" },
  login_failed: { label: "had a failed login attempt", group: "logins", icon: ShieldAlert, tone: "bad" },
  login_blocked_lockout: { label: "was blocked by the login lockout", group: "logins", icon: ShieldAlert, tone: "bad" },
  item_created: { label: "added an item", group: "items", icon: FileText, tone: "ok" },
  item_updated: { label: "edited an item", group: "items", icon: FileText, tone: "neutral" },
  item_renewed: { label: "marked an item renewed", group: "items", icon: RefreshCw, tone: "ok" },
  item_deleted: { label: "deleted an item", group: "items", icon: FileText, tone: "bad" },
  category_created: { label: "added a category", group: "items", icon: FileText, tone: "ok" },
  category_updated: { label: "edited a category", group: "items", icon: FileText, tone: "neutral" },
  category_deleted: { label: "deleted a category", group: "items", icon: FileText, tone: "bad" },
  invite_sent: { label: "invited a teammate", group: "team", icon: Users, tone: "ok" },
  invite_revoked: { label: "revoked an invitation", group: "team", icon: Users, tone: "neutral" },
  invite_accepted: { label: "joined the team", group: "team", icon: Users, tone: "ok" },
  member_removed: { label: "removed a teammate", group: "team", icon: Users, tone: "bad" },
  role_changed: { label: "changed a teammate's role", group: "team", icon: Users, tone: "neutral" },
  org_updated: { label: "updated organization settings", group: "settings", icon: Settings2, tone: "neutral" },
  profile_updated: { label: "updated their profile", group: "settings", icon: Settings2, tone: "neutral" },
  password_changed: { label: "changed their password", group: "logins", icon: KeyRound, tone: "neutral" },
  password_reset_requested: { label: "requested a password reset", group: "logins", icon: KeyRound, tone: "neutral" },
  password_reset_completed: { label: "reset their password", group: "logins", icon: KeyRound, tone: "ok" },
  data_exported: { label: "exported organization data", group: "settings", icon: Download, tone: "neutral" },
};

const TONE_CLASSES = {
  ok: "bg-[hsl(var(--status-ok)/0.12)] text-[hsl(var(--status-ok))]",
  bad: "bg-[hsl(var(--status-overdue)/0.12)] text-[hsl(var(--status-overdue))]",
  neutral: "bg-secondary text-muted-foreground",
};

export const ActivityTab = () => {
  const [logs, setLogs] = useState(null);
  const [group, setGroup] = useState("all");
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState(50);

  const load = useCallback(async () => {
    const res = await api.get("/audit?limit=500");
    setLogs(res.data.logs);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!logs) return [];
    let r = logs;
    if (group !== "all") r = r.filter((l) => (ACTION_META[l.action]?.group || "settings") === group);
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      r = r.filter((l) =>
        (l.actor_email || "").toLowerCase().includes(q) ||
        (l.details || "").toLowerCase().includes(q) ||
        (ACTION_META[l.action]?.label || l.action).toLowerCase().includes(q)
      );
    }
    return r;
  }, [logs, group, search]);

  if (!logs) {
    return <div className="space-y-3 max-w-3xl">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>;
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Activity</CardTitle>
          <CardDescription>
            Who did what, and when — logins and every change to your organization's records. Only owners and admins can see this.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => { setSearch(e.target.value); setVisible(50); }}
                placeholder="Search by person or detail…" className="pl-9 rounded-lg" data-testid="activity-search-input" />
            </div>
            <Select value={group} onValueChange={(v) => { setGroup(v); setVisible(50); }}>
              <SelectTrigger className="w-full sm:w-[170px] rounded-lg" data-testid="activity-group-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All activity</SelectItem>
                <SelectItem value="logins">Logins & security</SelectItem>
                <SelectItem value="items">Items & categories</SelectItem>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="settings">Settings & data</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <EmptyState icon={Activity} title="No activity to show"
              body={search || group !== "all" ? "Try a different search or filter." : "Actions like logins, item changes and team updates will appear here."}
              testId="activity-empty-state" />
          ) : (
            <>
              <ul className="divide-y divide-border" data-testid="activity-log-list">
                {filtered.slice(0, visible).map((log) => {
                  const meta = ACTION_META[log.action] || { label: log.action.replaceAll("_", " "), icon: Activity, tone: "neutral" };
                  const Icon = meta.icon;
                  return (
                    <li key={log.id} className="flex items-start gap-3 py-3">
                      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${TONE_CLASSES[meta.tone]}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm leading-snug">
                          <span className="font-medium">{log.actor_email || "Someone"}</span>{" "}
                          <span className="text-muted-foreground">{meta.label}</span>
                        </p>
                        {log.details && <p className="text-xs text-muted-foreground mt-0.5 break-words">{log.details}</p>}
                        <p className="text-xs text-muted-foreground/70 mt-0.5 tabular-nums">{fmtDateTime(log.created_at)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {filtered.length > visible && (
                <div className="pt-3 text-center">
                  <Button variant="secondary" className="rounded-xl" onClick={() => setVisible((v) => v + 50)}
                    data-testid="activity-load-more-button">
                    Show more ({filtered.length - visible} older)
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
